import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Github, Heart, Mail } from 'lucide-react';
import { useLanguageRouter } from '../utils/i18nRouter';

export const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { getLocalizedPath } = useLanguageRouter();

  return (
    <footer className="bg-white border-t border-gray-200 dark:bg-gray-900 dark:border-gray-700">
      <div className="container mx-auto px-6 py-12">
        {/* Tool Categories Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 gap-8 mb-12">
          {/* Developer Tools */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              {t('categories.developer')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to={getLocalizedPath('/tools/developer/json-formatter')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.jsonFormatter.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/developer/base64')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.base64.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/developer/uuid-generator')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.uuidGenerator.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/developer/url-encoder')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.urlEncoder.name')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Converter Tools */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              {t('categories.converter')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to={getLocalizedPath('/tools/converter/color-converter')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.colorConverter.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/converter/timestamp-converter')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.timestampConverter.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/converter/qr-generator')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.qrGenerator.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/converter/unit-converter')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.unitConverter.name')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Text Tools */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              {t('categories.text')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to={getLocalizedPath('/tools/text/markdown-editor')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.markdownEditor.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/text/text-diff')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.textDiff.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/text/password-generator')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.passwordGenerator.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/text/word-counter')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.wordCounter.name')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Network Tools */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              {t('categories.network')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to={getLocalizedPath('/tools/network/whois-lookup')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.whoisLookup.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/network/dns-lookup')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.dnsLookup.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/network/ip-lookup')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.ipLookup.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/network/ping-test')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.pingTest.name')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Image Tools */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              {t('categories.image')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to={getLocalizedPath('/tools/image/image-compress')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.imageCompress.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/image/image-resize')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.imageResize.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/image/image-crop')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.imageCrop.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/image/image-watermark')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.imageWatermark.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/network/ip-lookup')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.ipLookup.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/image/image-watermark')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.imageWatermark.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/math/calculator')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.calculator.name')}
                </Link>
              </li>
            </ul>
          </div>

          {/* Math Tools */}
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
              {t('categories.math')}
            </h3>
            <ul className="space-y-3">
              <li>
                <Link to={getLocalizedPath('/tools/math/calculator')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.calculator.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/math/percentage-calculator')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.percentageCalculator.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/math/unit-converter')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.unitConverter.name')}
                </Link>
              </li>
              <li>
                <Link to={getLocalizedPath('/tools/math/random-generator')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                  {t('tools.randomGenerator.name')}
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom Section - Logo, Description, Contact */}
        <div className="border-t border-gray-200 dark:border-gray-700 pt-8">
          <div className="w-full">
            {/* Brand and Description - Full Width */}
            <div className="w-full mb-8">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-6 lg:space-y-0">
                {/* Left: Brand and Description */}
                <div className="flex-1 lg:max-w-2xl">
                  <Link to={getLocalizedPath('/')} className="inline-block mb-4">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
                        <span className="text-white font-bold text-lg">TC</span>
                      </div>
                      <span className="text-xl font-bold text-gray-900 dark:text-white">
                        {t('header.title')}
                      </span>
                    </div>
                  </Link>
                  <p className="text-gray-600 dark:text-gray-400 mb-4 leading-relaxed">
                    {t('footer.description')}
                  </p>
                  <div className="text-sm text-gray-500 dark:text-gray-500">
                    <p className="mb-2">{t('footer.stats', { tools: '100+', categories: '6' })}</p>
                    <p>{t('footer.tagline')}</p>
                  </div>
                </div>

                {/* Right: Contact and Links */}
                <div className="flex flex-col space-y-6 lg:items-end">
                  <div className="flex flex-col sm:flex-row items-start lg:items-end space-y-4 sm:space-y-0 sm:space-x-4">
                    <a
                      href="https://github.com/fushui1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                      aria-label={t('footer.github')}
                    >
                      <Github className="w-4 h-4 mr-2" />
                      {t('footer.github')}
                    </a>
                    <a
                      href="mailto:1481983952@qq.com"
                      className="inline-flex items-center px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-primary-100 dark:hover:bg-primary-900/20 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200"
                      aria-label={t('footer.contact')}
                    >
                      <Mail className="w-4 h-4 mr-2" />
                      {t('footer.contact')}
                    </a>
                  </div>

                  <div className="flex flex-wrap items-center space-x-6">
                    <Link to={getLocalizedPath('/privacy')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                      {t('footer.privacy')}
                    </Link>
                    <Link to={getLocalizedPath('/terms')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                      {t('footer.terms')}
                    </Link>
                    <Link to={getLocalizedPath('/sitemap')} className="text-gray-600 hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400 transition-colors text-sm">
                      {t('footer.sitemap')}
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Bottom Bar - Full Width */}
            <div className="w-full pt-6 border-t border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                <div className="flex items-center text-gray-500 dark:text-gray-500 text-xs">
                  <span>{t('footer.builtWith')}</span>
                  <Heart className="w-3 h-3 mx-1 text-red-500" />
                  <span>{t('footer.openSource')}</span>
                </div>

                <div className="text-xs text-gray-500 dark:text-gray-500">
                  {t('footer.version')} 2.1.0 | {t('footer.lastUpdated')} 2025-01-29
                </div>
              </div>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
            <p className="text-gray-600 dark:text-gray-400 text-sm">
              {t('footer.copyright')}
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
};
